import type { FormikErrors, FormikHelpers, FormikValues } from "formik";
import { type FC, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { saveFormRecord, saveInternalFormRecord } from "../../api";
import { Form } from "../../components/Form/Form";
import { FormSubmittedMessage } from "../../components/FormSubmittedMessage/FormSubmittedMessage";
import { buildPreferences } from "../../config/preferences";
import { clearCheckpoint } from "../../helpers/checkpoints";
import { removeRecordDataQueries } from "../../helpers/recordDataQueries";
import { getFormattedRedirectUrl } from "../../helpers/structure";
import { getEffectiveSubmissionConfig } from "../../helpers/submissionPage";
import { PrefillProvider } from "../../hooks/PrefillProvider";
import { FormContext, type FormContextValue } from "../../hooks/useFormContext";
import { convertFormValuesToApi } from "../../registry/converter";
import type { FormConfig, FormValues } from "../../types/form";
import { navigateTo } from "../../utils/navigation";

export interface FormDetailProps {
  accountId: string;
  config: FormConfig;
  hash?: string;
  isInternal?: boolean;
  isPreview?: boolean;
  passcode?: string | null;
  reportId?: string;
}

export const FormDetail: FC<FormDetailProps> = ({
  accountId,
  config,
  hash,
  isInternal,
  isPreview,
  passcode,
  reportId,
}) => {
  const formState = config?.form_state;
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleOnSubmit = async (
    values: FormValues,
    formik: FormikHelpers<FormikValues>,
  ): Promise<void> => {
    formik.setStatus(null);

    try {
      const record = convertFormValuesToApi(values, config);
      if (isInternal && reportId) {
        await saveInternalFormRecord(accountId, reportId, record);
      } else {
        await saveFormRecord(accountId, hash ?? config.sharing_hash, record, !!isPreview, passcode);
      }
      const checkpointKey = isInternal && reportId ? reportId : (hash ?? config.sharing_hash);
      if (formState?.has_progress_restore) clearCheckpoint(accountId, checkpointKey);

      const submissionConfig = getEffectiveSubmissionConfig(formState);
      if (submissionConfig.mode === "redirect" && submissionConfig.redirect_url) {
        const redirectUrl = getFormattedRedirectUrl(submissionConfig.redirect_url);
        if (window !== window.parent) {
          window.parent.postMessage(`redirect: ${redirectUrl}`, "*");
        } else {
          navigateTo(redirectUrl);
        }
        return;
      }

      setSubmitted(true);
    } catch (error: any) {
      if (error?.response?.status === 400) {
        // The backend's 400 body is a { [fieldSlug]: message } map, but an
        // intermediary (proxy, WAF) can return HTML/string/array bodies —
        // those must not reach formik.setErrors.
        const data: unknown = error?.response?.data;
        const isFieldErrorMap =
          typeof data === "object" &&
          data !== null &&
          !Array.isArray(data) &&
          Object.values(data).every(
            (v) =>
              typeof v === "string" || (Array.isArray(v) && v.every((i) => typeof i === "string")),
          );
        if (isFieldErrorMap) {
          formik.setErrors(data as FormikErrors<FormikValues>);
        } else {
          console.warn("Unexpected 400 response body shape on form submit.", data);
          formik.setStatus("error");
        }
      } else if (error?.response?.status === 404) {
        navigate("../", { replace: true });
      } else {
        formik.setStatus("error");
      }
    }

    return await Promise.resolve();
  };

  // The submission just changed records on the server (e.g. a record linked through a single
  // entry backlink is now taken), but the record queries are cached for the life of the page.
  // Drop them here, while no form is mounted, so the next response starts from fresh data
  // (BUG-21774).
  const handleSubmitAnother = (): void => {
    removeRecordDataQueries(queryClient);
    setSubmitted(false);
  };

  const preferences = useMemo(() => buildPreferences(config.current_user), [config.current_user]);

  const context: FormContextValue = useMemo(
    () => ({ config, accountId, passcode, isInternal: isInternal ?? false, reportId, preferences }),
    [config, accountId, passcode, isInternal, reportId, preferences],
  );

  return (
    <FormContext.Provider value={context}>
      <PrefillProvider applicationStructure={config?.application?.structure} config={config}>
        {submitted ? (
          <FormSubmittedMessage config={config} onSubmitAnother={handleSubmitAnother} />
        ) : (
          <Form
            accountId={accountId}
            hash={hash ?? config.sharing_hash}
            config={config}
            onSubmit={handleOnSubmit}
          />
        )}
      </PrefillProvider>
    </FormContext.Provider>
  );
};
